"use client";

import { useEffect, useRef } from "react";

/* Scroll-linked decoration of the process section (desktop only), ported from the prototype:
   - the green segment on both page lines (Figma "progresso" 12375:9996/9997: x 178 / 1740, y 428, 1 × 139, #7bc78f)
     travels down the lines as the card stack scrolls by;
   - the ruler at the foot of the section (Figma 12375:10000 "régua (embaixo, virada)", 1900 × 43 at x 10) runs
     sideways with the scroll, like a lens focus ring. Ticks every 9 px (8 tall), a major tick every 189 px (16 tall),
     numbers 01… above the majors; #111112 at 20 % / 40 %; edges faded with Figma's mask (0 → 25.5 % → 71.5 % → 0). */

const TOP = 428; // Figma px
const SEG = 139;
const FOOT = 180; // section bottom padding

export default function QeProcessMotion() {
  const segL = useRef<HTMLElement>(null);
  const segR = useRef<HTMLElement>(null);
  const ruler = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sec = segL.current?.closest("section");
    if (!sec) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    const up = () => {
      raf = 0;
      const k = parseFloat(getComputedStyle(document.documentElement).fontSize) / 8; // CSS px per Figma px
      const r = sec.getBoundingClientRect();
      const max = sec.offsetHeight - (TOP + SEG + FOOT) * k;
      const p = Math.max(0, Math.min(1, (window.innerHeight * 0.5 - r.top - TOP * k) / max));
      const y = `translateY(${(p * max).toFixed(1)}px)`;
      if (segL.current) segL.current.style.transform = y;
      if (segR.current) segR.current.style.transform = y;
      const b = ruler.current?.getBoundingClientRect();
      if (b && track.current && b.bottom > -50 && b.top < window.innerHeight + 50) {
        track.current.style.transform = `translateX(${(-(window.innerHeight - b.top) * 0.45 + 137 * k).toFixed(1)}px)`;
      }
    };
    const on = () => { if (!raf) raf = requestAnimationFrame(up); };
    up();
    if (reduce) return;
    window.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", on);
      window.removeEventListener("resize", on);
    };
  }, []);

  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 hidden lg:block">
      <i ref={segL} className="absolute top-[53.5rem] left-[22.25rem] h-[17.375rem] w-px bg-[#7bc78f] will-change-transform" />
      <i ref={segR} className="absolute top-[53.5rem] left-[217.5rem] h-[17.375rem] w-px bg-[#7bc78f] will-change-transform" />
      <div
        ref={ruler}
        className="absolute bottom-0 left-[1.25rem] h-[5.375rem] w-[237.5rem] overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_25.5%,#000_71.5%,transparent)]">
        <div
          ref={track}
          className="absolute bottom-0 left-0 h-full w-[1181.25rem] will-change-transform"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg,rgba(17,17,18,.2) 0 1px,transparent 1px 1.125rem),repeating-linear-gradient(90deg,rgba(17,17,18,.2) 0 1px,transparent 1px 23.625rem)",
            backgroundSize: "100% 1rem, 100% 2rem",
            backgroundPosition: "0 100%, 0 100%",
            backgroundRepeat: "no-repeat",
          }}>
          {Array.from({ length: 50 }, (_, i) => (
            <span
              key={i}
              className="absolute top-[1.08rem] w-[5rem] -translate-x-1/2 text-center font-geist text-[1.125rem] leading-[1.125rem] font-semibold tracking-[-0.04em] text-[#111112]/40"
              style={{ left: `${i * 23.625}rem` }}>
              {String(i + 1).padStart(2, "0")}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
